import * as http from "api/http";
import { SERVICES_URL, handleError } from "api/helper";
import { AuthService } from "@adfenix/types";

export type UserStatus =
  | "FORCE_CHANGE_PASSWORD"
  | "RESET_REQUIRED"
  | "CONFIRMED"
  | "UNKNOWN"
  | "SSO_USER";

export interface UserStatusResponse {
  userStatus: UserStatus;
  userName: string;
  latestResendPasswordUtc: string;
}
const post = http.post;
const get = http.get;
const del = http.del;
const put = http.put;

const baseUrl = "api/auth/v1";
const authedBaseUrl = "auth/auth/v1";

const authPublicBaseUrl = `/auth/public/v1`;
const authIdpBaseUrl = `${authPublicBaseUrl}/idp-config`;

export const resendEmail = ({ email }: { email: string }): Promise<void> =>
  post(`${SERVICES_URL}/${baseUrl}/user/password/resend`, { email }).catch(
    handleError
  );

export const resendEmailAuthed = ({
  email
}: {
  email: string;
}): Promise<void> =>
  post(`${SERVICES_URL}/${authedBaseUrl}/user/${email}/resend-password`).catch(
    handleError
  );

export const exchangeEmailForToken = (
  propertyId: string | undefined,
  type: "UserId" | "LegacyToken",
  userAccess?: string
): Promise<{
  expiryDate: string;
  key: string;
  value: string;
}> =>
  get(
    `${SERVICES_URL}/${baseUrl}/token/${propertyId}/${type}/${userAccess}`
  ).catch(handleError);

export const getUserStatus = (email: string): Promise<UserStatusResponse> =>
  get(`${SERVICES_URL}/${baseUrl}/user/${email}`).catch(handleError);

export const getUserStatusAuthed = (
  email: string
): Promise<UserStatusResponse> =>
  get(`${SERVICES_URL}/${authedBaseUrl}/user/idp-status/${email}`).catch(
    handleError
  );

export const getUserStatusPublic = (
  email: string
): Promise<UserStatusResponse> =>
  get(`${SERVICES_URL}${authPublicBaseUrl}/user/idp-status/${email}`).catch(
    handleError
  );

export const createUser = (
  addUserModel: AuthService.CreateUserV2Request
): Promise<void> =>
  post(`${SERVICES_URL}/${authedBaseUrl}/user`, addUserModel).catch(
    handleError
  );

export const updateUserRole = ({
  updateUserModel,
  userId
}: {
  updateUserModel: AuthService.UpdateUserRoleRequest;
  userId: string;
}): Promise<void> =>
  put(
    `${SERVICES_URL}/${authedBaseUrl}/user/${userId}/role`,
    updateUserModel
  ).catch(handleError);

export const getRoles = (): Promise<AuthService.RoleDto[]> =>
  get(`${SERVICES_URL}/${authedBaseUrl}/role`).catch(handleError);

export const getUser = (email: string): Promise<AuthService.UserDto> =>
  get(`${SERVICES_URL}/${authedBaseUrl}/user/${email}`).catch(handleError);

export const deleteUser = (id: string): Promise<AuthService.UserDto["id"]> =>
  del(`${SERVICES_URL}/${authedBaseUrl}/user/${id}`).catch(handleError);

export const getRole = (id: string): Promise<AuthService.RoleDto> =>
  get(`${SERVICES_URL}/${authedBaseUrl}/role/${id}`).catch(handleError);

export const createRole = (
  createRoleModel: AuthService.CreateRoleRequest
): Promise<AuthService.RoleDto["id"]> =>
  post(`${SERVICES_URL}/${authedBaseUrl}/role`, createRoleModel).catch(
    handleError
  );

export const updateRole = ({
  id,
  updateRoleModel
}: {
  id: AuthService.RoleDto["id"];
  updateRoleModel: AuthService.UpdateRoleRequest;
}): Promise<AuthService.RoleDto["id"]> =>
  put(`${SERVICES_URL}/${authedBaseUrl}/role/${id}`, updateRoleModel).catch(
    handleError
  );

export const getPermissions = (): Promise<AuthService.PermissionDto[]> =>
  get(`${SERVICES_URL}/${authedBaseUrl}/permission`).catch(handleError);

export const createInIdp = (userId: string): Promise<string> =>
  post(`${SERVICES_URL}/${authedBaseUrl}/user/${userId}/create-in-idp`).catch(
    handleError
  );

export const getCognitoConfig = (
  email: string
): Promise<AuthService.IdentityProviderPublicResponse> =>
  get(`${SERVICES_URL}${authIdpBaseUrl}/${email}`).catch(handleError);

export const getCognitoConfigById = (
  id: string | undefined
): Promise<AuthService.IdentityProviderPublicResponse> =>
  get(`${SERVICES_URL}${authIdpBaseUrl}/idp/${id}`).catch(handleError);

/**
 * We no longer use intercom but Hotjar is also using this data.
 */
export const getUserDataForUserTracking =
  (): Promise<AuthService.UserDataForIntercomResponse> =>
    get(`${SERVICES_URL}/${authedBaseUrl}/user/intercom/data`).catch(
      handleError
    );

export const migrateHqUsersToNewCampaignHub = (hqId: string): Promise<string> =>
  post(
    `${SERVICES_URL}/${authedBaseUrl}/user/publish-user-created-event/${hqId}`
  ).catch(handleError);

export const stopMasquerading = (): Promise<void> =>
  put(`${SERVICES_URL}/${authedBaseUrl}/user/stop-masquerading`).catch(
    handleError
  );

export const startMasquerading = (userId: string): Promise<void> =>
  put(
    `${SERVICES_URL}/${authedBaseUrl}/user/start-masquerading-as/${userId}`
  ).catch(handleError);
